import { Component, DestroyRef, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { PackagingUnitInfoDto } from 'src/app/data-transfer/entities/packaging-unit-entities/packaging-unit-info-dto';
import { TagDto } from 'src/app/data-transfer/entities/tag-dto';
import { Gs1DataTransferApiService } from 'src/app/data-transfer/services/gs1-data-transfer-api-service';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { PackagingUnitsOverviewComponent } from '../packaging-units-overview/packaging-units-overview.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { PackagingSystemInfoDto } from 'src/app/data-transfer/entities/packaging-system-entities/packaging-system-info-dto';
import { PackagingSystemsOverviewComponent } from '../packaging-systems-overview/packaging-systems-overview.component';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { MatDialog } from '@angular/material/dialog';
import { AnalysisNotPossibleDialogComponent } from 'src/app/components/dialogs/analysis-not-possible-dialog/analysis-not-possible-dialog.component';
import { TranslocoService } from '@jsverse/transloco';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { PAGE_SIZE_OPTIONS } from 'src/app/model/pagination-defaults';

@Component({
  selector: 'app-gs1-data-transfer',
  templateUrl: './gs1-data-transfer.component.html',
  styleUrls: ['./gs1-data-transfer.component.scss'],
})
export class GS1DataTransferComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  @ViewChild('packagingUnitsReadyForTransferTable') private packagingUnitsReadyForTransferTable!: PackagingUnitsOverviewComponent;
  @ViewChild('packagingUnitsTransferredTable') private packagingUnitsTransferredTable!: PackagingUnitsOverviewComponent;
  @ViewChild('packagingSystemsReadyForTransferTable') private packagingSystemsReadyForTransferTable!: PackagingSystemsOverviewComponent;
  @ViewChild('packagingSystemsTransferredTable') private packagingSystemsTransferredTable!: PackagingSystemsOverviewComponent;

  private routeDataSubscription?: Subscription;

  allTags: TagDto[] = [];

  displayedColumnsReadyForTransfer = ['directoryName', 'id', 'brandName', 'productName', 'articleNumber', 'supplier', 'tags', 'action', 'select'];
  displayedColumnsRequiresReview = ['directoryName', 'id', 'brandName', 'productName', 'articleNumber', 'supplier', 'tags', 'infoButton', 'action'];
  displayedColumnsTransferred = ['directoryName', 'id', 'brandName', 'productName', 'articleNumber', 'supplier', 'tags', 'action', 'select'];
  displayedColumnsProcessing = ['directoryName', 'id', 'brandName', 'productName', 'articleNumber', 'supplier', 'tags'];


  packagingUnitsReadyForTransferDataSource: PaginationDataSource<PackagingUnitInfoDto>;
  packagingUnitsRequiringReviewDataSource: PaginationDataSource<PackagingUnitInfoDto>;
  packagingUnitsTransferredDataSource: PaginationDataSource<PackagingUnitInfoDto>;
  isPackagingUnitReadyForTransferSelected = false;
  isPackagingUnitTransferredSelected = false;

  packagingSystemsReadyForTransferDataSource: PaginationDataSource<PackagingSystemInfoDto>;
  packagingSystemsRequiringReviewDataSource: PaginationDataSource<PackagingSystemInfoDto>;
  packagingSystemsTransferredDataSource: PaginationDataSource<PackagingSystemInfoDto>;
  isPackagingSystemReadyForTransferSelected = false;
  isPackagingSystemTransferredSelected = false;

  // data for processing table
  packagingUnitsProcessingDataSource = new PaginationDataSource<PackagingUnitInfoDto>(x => this.gs1DataTransferApiService.getPackagingUnitsProcessing(x));
  packagingSystemsProcessingDataSource = new PaginationDataSource<PackagingSystemInfoDto>(x => this.gs1DataTransferApiService.getPackagingSystemsProcessing(x));

  packagingUnitsProcessingCount = signal(0);
  packagingSystemsProcessingCount = signal(0);
  isRefreshing = signal(false);

  constructor(private route: ActivatedRoute, private gs1DataTransferApiService: Gs1DataTransferApiService, private spinner: NgxSpinnerService, private dialog: MatDialog, private translateService: TranslocoService) {
    this.packagingUnitsReadyForTransferDataSource = new PaginationDataSource<PackagingUnitInfoDto>(x => gs1DataTransferApiService.getPackagingUnitsReadyForTransfer(x));
    this.packagingUnitsRequiringReviewDataSource = new PaginationDataSource<PackagingUnitInfoDto>(x => gs1DataTransferApiService.getPackagingUnitsRequiringReview(x));
    this.packagingUnitsTransferredDataSource = new PaginationDataSource<PackagingUnitInfoDto>(x => gs1DataTransferApiService.getTransferredPackagingUnits(x));

    this.packagingSystemsReadyForTransferDataSource = new PaginationDataSource<PackagingSystemInfoDto>(x => gs1DataTransferApiService.getPackagingSystemsReadyForTransfer(x));
    this.packagingSystemsRequiringReviewDataSource = new PaginationDataSource<PackagingSystemInfoDto>(x => gs1DataTransferApiService.getPackagingSystemsRequiringReview(x));
    this.packagingSystemsTransferredDataSource = new PaginationDataSource<PackagingSystemInfoDto>(x => gs1DataTransferApiService.getTransferredPackagingSystems(x));
  }

  onTransferPackagingUnits() {
    const selectedPackagingUnitIds = this.packagingUnitsReadyForTransferTable.selectedPackagingUnitIds;
    this.packagingUnitsReadyForTransferTable.selectedPackagingUnitIds = [];
    this.transferPackagingUnits(selectedPackagingUnitIds);
  }

  onRetransferPackagingUnits() {
    const selectedPackagingUnitIds = this.packagingUnitsTransferredTable.selectedPackagingUnitIds;
    this.packagingUnitsTransferredTable.selectedPackagingUnitIds = [];
    this.transferPackagingUnits(selectedPackagingUnitIds);
  }

  onTransferPackagingSystems() {
    const selectedPackagingSystemIds = this.packagingSystemsReadyForTransferTable.selectedPackagingSystemIds;
    this.packagingSystemsReadyForTransferTable.selectedPackagingSystemIds = [];
    this.transferPackagingSystems(selectedPackagingSystemIds);
  }

  onRetransferPackagingSystems() {
    const selectedPackagingSystemIds = this.packagingSystemsTransferredTable.selectedPackagingSystemIds;
    this.packagingSystemsTransferredTable.selectedPackagingSystemIds = [];
    this.transferPackagingSystems(selectedPackagingSystemIds);
  }

  showPackagingUnitProblemInformation(packagingUnit: PackagingUnitInfoDto) {
    this.spinner.show();
    this.gs1DataTransferApiService.getPackagingUnitProblemInformation(packagingUnit.id).subscribe(problemInformation => {
      this.spinner.hide();
      const generalExplanation = this.translateService.translate("dataManagement.gs1DataTransfer.problemInformationGeneralExplanation");
      const alertDialogConfig = getDialogConfig({ generalExplanation: generalExplanation, problemInformation: problemInformation }, '600px');
      this.dialog.open(AnalysisNotPossibleDialogComponent, alertDialogConfig);
    });
  }

  showPackagingSystemProblemInformation(packagingSystem: PackagingSystemInfoDto) {
    this.spinner.show();
    this.gs1DataTransferApiService.getPackagingSystemProblemInformation(packagingSystem.id).subscribe(problemInformation => {
      this.spinner.hide();
      const generalExplanation = this.translateService.translate("dataManagement.gs1DataTransfer.problemInformationGeneralExplanation");
      const alertDialogConfig = getDialogConfig({ generalExplanation: generalExplanation, problemInformation: problemInformation }, '600px');
      this.dialog.open(AnalysisNotPossibleDialogComponent, alertDialogConfig);
    });
  }

  private transferPackagingUnits(packagingUnitIds: number[]) {
    this.spinner.show();
    this.gs1DataTransferApiService.transferPackagingUnits(packagingUnitIds).subscribe(transferResult => {
      this.spinner.hide();

      const message =
      this.translateService.translate('dataManagement.gs1DataTransfer.packagingUnitsTransferredMessage',
                                      { successCount: transferResult.successfullyTransferredItems,
                                        totalCount: transferResult.successfullyTransferredItems + transferResult.notTransferredItems });

      const data: SimpleDialogData = {
        title: this.translateService.translate('dataManagement.gs1DataTransfer.transferCompletedDialogTitle'),
        messages: [message],
        icon: 'info'
      };

      this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(data))
    });
  }

  private transferPackagingSystems(packagingSystemIds: number[]) {
    this.spinner.show();
    this.gs1DataTransferApiService.transferPackagingSystems(packagingSystemIds).subscribe(transferResult => {
      this.spinner.hide();

      const message =
      this.translateService.translate('dataManagement.gs1DataTransfer.packagingSystemsTransferredMessage',
                                      { successCount: transferResult.successfullyTransferredItems,
                                        totalCount: transferResult.successfullyTransferredItems + transferResult.notTransferredItems });

      const data: SimpleDialogData = {
        title: this.translateService.translate('dataManagement.gs1DataTransfer.transferCompletedDialogTitle'),
        messages: [message],
        icon: 'info'
      };

      this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(data))
    });
  }

  private get allDataSources() {
    return [
      this.packagingUnitsProcessingDataSource,
      this.packagingUnitsRequiringReviewDataSource,
      this.packagingUnitsReadyForTransferDataSource,
      this.packagingUnitsTransferredDataSource,
      this.packagingSystemsProcessingDataSource,
      this.packagingSystemsRequiringReviewDataSource,
      this.packagingSystemsReadyForTransferDataSource,
      this.packagingSystemsTransferredDataSource,
    ];
  }

  refreshAll() {
    this.allDataSources.forEach(dataSource => dataSource.refresh());
  }

  ngOnInit(): void {
    this.routeDataSubscription = this.route.data
      .subscribe(data => {
        this.allTags = data.allTags;
      });

    this.packagingUnitsProcessingDataSource.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(page => this.packagingUnitsProcessingCount.set(page.totalItemCount));
    this.packagingSystemsProcessingDataSource.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(page => this.packagingSystemsProcessingCount.set(page.totalItemCount));

    combineLatest(this.allDataSources.map(dataSource => dataSource.isLoading))
      .pipe(map(states => states.some(isLoading => isLoading)), takeUntilDestroyed(this.destroyRef))
      .subscribe(isLoading => this.isRefreshing.set(isLoading));

    this.packagingUnitsProcessingDataSource.fetchData(0, PAGE_SIZE_OPTIONS.DEFAULT[0]);
    this.packagingSystemsProcessingDataSource.fetchData(0, PAGE_SIZE_OPTIONS.DEFAULT[0]);
  }

  ngOnDestroy(): void {
    this.routeDataSubscription?.unsubscribe();
  }

}
